<template>
  <div class="noi-card">
    <div class="noi-card__top-line"></div>

    <div class="noi-card__header">
      <span class="noi-card__label">NOI del periodo<CollectedInfo :park-id="parkId" :period="periodKey" basis="due" title="¿Cómo se calcula el NOI?" :paragraphs="noiInfo" /></span>
      <span class="noi-card__sublabel">Ingreso operativo neto</span>
    </div>

    <template v-if="!comparisonMode">
      <button class="noi-card__body" @click="expanded = !expanded">
        <span class="noi-card__value">{{ formatCurrency(period?.noiTotal) }}</span>
        <div class="noi-card__sub-col">
          <span class="noi-card__sub noi-card__sub--equation">
            {{ formatCompact(period?.collectedTotal) }} cobrado − {{ formatCompact(period?.expensesTotal) }} gastos
          </span>
          <span class="noi-card__sub" :class="trendDiff >= 0 ? 'noi-card__sub--good' : 'noi-card__sub--bad'">
            {{ trendDiff >= 0 ? '▲' : '▼' }} {{ formatCompact(Math.abs(trendDiff)) }} vs {{ previousPeriodLabel }}
          </span>
          <span class="noi-card__sub noi-card__sub--neutral">Acumulado {{ currentYearLabel }}: {{ formatCompact(yearToDateNOI) }}</span>
          <span v-if="period?.usd?.collected > 0" class="noi-card__sub noi-card__sub--neutral">Aparte, en dólares: US$ {{ Math.round(period.usd.collected).toLocaleString('es-MX') }} cobrados</span>
        </div>
      </button>

      <div v-if="expanded" class="noi-card__expand">
        <div class="noi-card__expand-row noi-card__expand-row--head">
          <span>Categoría</span>
          <span>Monto</span>
        </div>
        <div v-for="g in expensesBreakdown?.groups || []" :key="g.key" class="noi-card__expand-row">
          <span>{{ g.label }}</span>
          <span class="noi-card__expand-amount">{{ formatCurrency(g.amount) }}</span>
        </div>
        <div class="noi-card__expand-row noi-card__expand-row--total">
          <span>Total gastos</span>
          <span class="noi-card__expand-amount">{{ formatCurrency(expensesBreakdown?.total) }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="noi-card__compare-row">
        <div class="noi-card__compare-col">
          <span class="noi-card__period-label">{{ periodALabel }}</span>
          <span class="noi-card__value noi-card__value--compact">{{ formatCurrency(period?.noiTotal) }}</span>
        </div>
        <div class="noi-card__compare-divider"></div>
        <div class="noi-card__compare-col">
          <span class="noi-card__period-label noi-card__period-label--muted">{{ periodBLabel }}</span>
          <span class="noi-card__value noi-card__value--compact noi-card__value--muted">{{ formatCurrency(comparisonSnapshot?.noiTotal) }}</span>
        </div>
      </div>
      <div class="noi-card__diff-row">
        <span class="noi-card__diff" :class="compareDiff >= 0 ? 'noi-card__sub--good' : 'noi-card__sub--bad'">
          {{ compareDiff >= 0 ? '▲' : '▼' }} {{ formatCurrency(Math.abs(compareDiff)) }}
        </span>
      </div>
    </template>
  </div>
</template>

<script setup>
import CollectedInfo from '@/components/Global/CollectedInfo.vue'
import { ref, computed } from 'vue'

const props = defineProps({
  parkId: { type: String, default: '' },
  periodKey: { type: String, default: '' },
  period: { type: Object, default: null },
  previousPeriod: { type: Object, default: null },
  previousPeriodLabel: { type: String, default: '' },
  yearToDateNOI: { type: Number, default: 0 },
  expensesBreakdown: { type: Object, default: null },
  comparisonMode: { type: Boolean, default: false },
  comparisonSnapshot: { type: Object, default: null },
  periodALabel: { type: String, default: '' },
  periodBLabel: { type: String, default: '' },
})

const expanded = ref(false)

const noiInfo = computed(() => [
  `NOI = cobrado − gastos del periodo.${props.period ? ` Este periodo: ${formatCurrency(props.period.collectedTotal)} − ${formatCurrency(props.period.expensesTotal)} = ${formatCurrency(props.period.noiTotal)}.` : ''}`,
  'Cobrado: lo ya abonado a los cargos de renta y cargos propios (manuales, penalizaciones) que pertenecen a este mes, igual que en Cobranza. Los servicios y el mantenimiento que se cobran a los inquilinos no entran aquí: son dinero que el parque paga y recupera, por eso tampoco se restan como gasto. Por esa razón esta cifra es menor que el Total cobrado de Cobranza. También cuentan contratos que ya terminaron, para no borrar ingresos reales del historial. Solo pesos mexicanos: lo cobrado en dólares se muestra aparte.',
  'Gastos: los gastos operativos registrados, más el mantenimiento que no se cobró a inquilinos (incluye naves sin contrato) y los servicios que el parque absorbió al exentar a un inquilino. Las facturas de servicios que sí se reparten a los inquilinos no son gasto del parque.',
])

const currentYearLabel = computed(() => String(props.period?.year || new Date().getFullYear()))

const trendDiff = computed(() => (props.period?.noiTotal ?? 0) - (props.previousPeriod?.noiTotal ?? 0))
const compareDiff = computed(() => (props.period?.noiTotal ?? 0) - (props.comparisonSnapshot?.noiTotal ?? 0))

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount)
}

function formatCompact(amount) {
  if (amount === undefined || amount === null) return '—'
  const abs = Math.abs(amount)
  if (abs >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `$${Math.round(amount / 1000)}K`
  return formatCurrency(amount)
}
</script>

<style scoped>
.noi-card {
  position: relative;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 0.6rem 1rem 0.55rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.noi-card__top-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #4338ca;
}

.noi-card__header {
  margin-bottom: 0.3rem;
}

.noi-card__label {
  display: block;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.72rem;
  color: #64748b;
}

.noi-card__sublabel {
  display: block;
  font-family: var(--font-body);
  font-size: 0.65rem;
  color: #94a3b8;
}

.noi-card__body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.noi-card__value {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 1.6rem;
  color: var(--text-content);
  flex-shrink: 0;
}

.noi-card__value--compact {
  font-size: 1.1rem;
  margin-bottom: 0;
}

.noi-card__value--muted {
  color: #94a3b8;
}

.noi-card__sub-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  text-align: right;
  min-width: 0;
}

.noi-card__sub {
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: #64748b;
  line-height: 1.35;
}

.noi-card__sub--equation {
  font-family: var(--font-mono);
}

.noi-card__sub--good {
  color: #10b981;
}

.noi-card__sub--bad {
  color: #e84545;
}

.noi-card__sub--neutral {
  color: #94a3b8;
}

.noi-card__expand {
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.noi-card__expand-row {
  display: flex;
  justify-content: space-between;
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--text-content);
}

.noi-card__expand-row--head {
  color: #94a3b8;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.noi-card__expand-row--total {
  border-top: 1px solid #f1f5f9;
  padding-top: 0.25rem;
  margin-top: 0.15rem;
  font-weight: 600;
}

.noi-card__expand-amount {
  font-family: var(--font-mono);
}

.noi-card__compare-row {
  display: flex;
  align-items: stretch;
  gap: 0.6rem;
}

.noi-card__compare-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.noi-card__compare-divider {
  width: 1px;
  background: #e2e8f0;
  align-self: stretch;
}

.noi-card__period-label {
  font-size: 0.65rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.noi-card__period-label--muted {
  color: #94a3b8;
}

.noi-card__diff-row {
  display: flex;
  justify-content: center;
  margin-top: 0.4rem;
  padding-top: 0.3rem;
  border-top: 1px solid #f1f5f9;
}

.noi-card__diff {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 600;
}
</style>